import { getDataGridToolbarProps } from 'features/shared/utility/dataGridToolbarHelper';
import usePermissions from 'features/shared/hooks/usePermissions';
import { PERMISSION_RESOURCES } from 'features/shared/constants/permissionResources';
import FormSpinner from 'components/FormSpinner';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Box } from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import { CommonDataGrid } from 'features/shared/components/table';
import { RMSConfirmationDialog } from '../../../shared/components/dialog';
import { RMSButton } from '../../../shared/components/buttons';
import { executeApiAction } from '../../../shared/utility/apiHelper';
import { notify } from 'reducers/NotifierReducer';
import useConfirmDialog from '../../../shared/utility/useConfirmDialog';
import PersonSopDialog from './PersonSopDialog';
import { getPeopleSops, deletePersonSop } from '../../services/sopService';
import { SOP_GRID_COLUMNS } from '../../constants/personSopsTabConstants';

const PersonSopsTab = ({ personId, onSopsChanged }) => {
  const dispatch = useDispatch();
  const canEditPerson = usePermissions(PERMISSION_RESOURCES.PERSON).Edit;
  const [loading, setLoading] = useState(false);
  const [sopLinks, setSopLinks] = useState([]);
  const [formOpen, setFormOpen] = useState(false);
  const [editingSopLink, setEditingSopLink] = useState(null);
  const { confirmState, requestConfirm, handleYes, handleNo } = useConfirmDialog();

  const loadSopLinks = useCallback(async () => {
    if (!personId) return;

    await executeApiAction({
      setLoading,
      dispatch,
      action: () => getPeopleSops(personId),
      onSuccess: (data) => setSopLinks(data),
    });
  }, [dispatch, personId]);

  const existingSopIds = useMemo(() => sopLinks.map((link) => link.ptsSOPID), [sopLinks]);

  const handleAdd = useCallback(() => {
    setEditingSopLink(null);
    setFormOpen(true);
  }, []);

  const handleEdit = useCallback((row) => {
    setEditingSopLink(row);
    setFormOpen(true);
  }, []);

  const handleFormClose = useCallback(() => {
    setFormOpen(false);
    setEditingSopLink(null);
  }, []);

  const handleSaved = useCallback(() => {
    handleFormClose();
    loadSopLinks();
    onSopsChanged?.();
  }, [handleFormClose, loadSopLinks, onSopsChanged]);

  const handleDelete = useCallback(
    (row) => {
      requestConfirm(
        `Are you sure you want to remove this "${row.SOP?.SOPID || 'SOP'}" record?`,
        async () => {
          await executeApiAction({
            setLoading,
            dispatch,
            action: () =>
              deletePersonSop({
                ptsParentID: personId,
                ParentType: 'Person',
                ptsSOPID: row.ptsSOPID,
              }),
            onSuccess: () => {
              dispatch(notify('SOP deleted successfully', 'success'));
              loadSopLinks();
              onSopsChanged?.();
            },
          });
        },
        'Delete SOP'
      );
    },
    [dispatch, personId, requestConfirm, loadSopLinks, onSopsChanged]
  );

  useEffect(() => {
    loadSopLinks();
  }, [loadSopLinks]);

  return (
    <Box className="person-names-tab__grid-wrap">
      {loading && <FormSpinner />}
      <Box className="person-addresses-tab__toolbar">
        <RMSButton
          label="Add SOP"
          prefixIcon={<AddIcon />}
          onClick={handleAdd}
          disabled={!canEditPerson}
        />
      </Box>

      <CommonDataGrid
        rows={sopLinks}
        columns={SOP_GRID_COLUMNS}
        getRowId={(row) => row.ptsSOPID}
        toolbar={getDataGridToolbarProps({ printTitle: 'SOPS' })}
        actions={{
          edit: { visible: true, onClick: handleEdit, disabled: !canEditPerson },
          delete: { visible: true, onClick: handleDelete, disabled: !canEditPerson },
        }}
      />

      {formOpen && (
        <PersonSopDialog
          open={formOpen}
          personId={personId}
          sopLink={editingSopLink}
          existingSopIds={existingSopIds}
          onClose={handleFormClose}
          onSaved={handleSaved}
        />
      )}

      {confirmState.open && (
        <RMSConfirmationDialog
          open={confirmState.open}
          title={confirmState.title}
          message={confirmState.message}
          onNo={handleNo}
          onYes={handleYes}
        />
      )}
    </Box>
  );
};

export default PersonSopsTab;
